/**
 * FK-Dashboard v5 (Timm, 2026-08-12): "Im FK-Dashboard soll ich einen wirklich
 * kompakten Überblick bekommen: Ist mein Team quasi grün, bezogen auf Retention
 * und Wirtschaftlichkeit? Dann kann ich in die Tiefe eintauchen."
 *
 * Die Seite beginnt deshalb mit einer ANTWORT, nicht mit Daten:
 * - Status-Kopf: zwei Karten „Bindung" und „Wirtschaftlichkeit" mit einem
 *   Zustand (im Soll / beobachten / handeln), dem Grund dafür, der Verteilung
 *   und dem Absprung in die jeweilige Tiefenseite.
 * - Alles Bisherige (v4, Marie 27.07.) liegt unverändert darunter, aber
 *   eingeklappt hinter „Details anzeigen":
 *   - Zeitraum frei wählbar (von–bis) + Presets als Schnellwahl
 *   - KPI-Kacheln mit Mini-Trendlinien (Sparklines) bzw. Vorperioden-Vergleich
 *   - Risiko-Verteilungs-Fluss (gestapelte Flächen gering/mittel/hoch)
 *   - Puls: Teilnahmequote statt nichtssagender Gesamt-Zustimmung
 *   - Muster: Signal-Rangliste + KI-Musteranalyse
 */
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Calculator,
  ChevronDown,
  CircleCheck,
  ClipboardList,
  Eye,
  MessageSquareWarning,
  Radar,
  RefreshCw,
  Sparkles,
  TrendingUp,
  UserMinus,
  UserPlus,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import {
  type WirtschaftAmpelLevel,
  aiApi,
  fkDashboardApi,
  surveyApi,
  wirtschaftApi,
} from "../lib/api.js";

const DAY_MS = 24 * 60 * 60 * 1000;
const iso = (d: Date) => d.toISOString().slice(0, 10);

const PRESETS = [
  { label: "30 Tage", days: 30 },
  { label: "90 Tage", days: 90 },
  { label: "180 Tage", days: 180 },
  { label: "12 Monate", days: 365 },
] as const;

const SEVERITY_COLOR: Record<string, string> = {
  critical: "var(--risk-high)",
  warning: "var(--risk-medium)",
  info: "var(--accent)",
};

// Risiko-Serien SIND Status → Status-Tokens (Marken-Farbe für Flächen/Balken).
const RISK_COLORS = {
  low: "var(--risk-low)",
  medium: "var(--risk-medium)",
  high: "var(--risk-high)",
} as const;

const RISK_LABELS: Record<string, string> = {
  low: "geringes Risiko",
  medium: "mittleres Risiko",
  high: "hohes Risiko",
};

// ── Status-Kopf ───────────────────────────────────────────────────────────────
// Schwellen bewusst als benannte Konstanten: sie sind eine fachliche Setzung
// (Timm, 12.08.) und sollen später pro Bereich konfigurierbar werden, ohne
// dass jemand die Ableitungslogik anfassen muss.

/** Ab so vielen kritischen Mitarbeitern steht die Karte auf Rot („handeln"). */
const ROT_AB_KRITISCH = 1;

/** Übersteigt der Gelb-Anteil diesen Wert, steht die Karte ebenfalls auf Rot. */
const ROT_AB_GELB_ANTEIL = 0.2;

type StatusLevel = "gruen" | "gelb" | "rot";

interface Verteilung {
  ok: number;
  warning: number;
  critical: number;
  total: number;
}

/** Wort, Marken-Farbe (Punkt/Balken) und Text-Farbe je Zustand. */
const STATUS_META: Record<StatusLevel, { wort: string; mark: string; text: string }> = {
  gruen: { wort: "im Soll", mark: "var(--risk-low)", text: "var(--risk-low-text)" },
  gelb: { wort: "beobachten", mark: "var(--risk-medium)", text: "var(--risk-medium-text)" },
  rot: { wort: "handeln", mark: "var(--risk-high)", text: "var(--risk-high-text)" },
};

/** Legenden-Beschriftung der drei Ampelstufen (Farbe steht nie allein). */
const AMPEL_META: ReadonlyArray<{
  key: keyof Omit<Verteilung, "total">;
  label: string;
  mark: string;
}> = [
  { key: "ok", label: "im Soll", mark: "var(--risk-low)" },
  { key: "warning", label: "auffällig", mark: "var(--risk-medium)" },
  { key: "critical", label: "kritisch", mark: "var(--risk-high)" },
];

/** Zählt eine Liste von Ampelstufen zu einer Verteilung zusammen. */
function zaehleAmpeln(levels: readonly WirtschaftAmpelLevel[]): Verteilung {
  const v: Verteilung = { ok: 0, warning: 0, critical: 0, total: levels.length };
  for (const level of levels) v[level] += 1;
  return v;
}

/**
 * Ableitungsregel für den Gesamtzustand einer Karte (Timm, 12.08.):
 * ROT („handeln"), wenn mindestens ein Mitarbeiter kritisch ist ODER mehr als
 * 20 % der Mitarbeiter auf Gelb stehen — eine breite gelbe Front ist genauso
 * ein Handlungsgrund wie ein einzelner harter Fall.
 * GELB („beobachten"), wenn es überhaupt gelbe Fälle gibt.
 * Sonst GRÜN („im Soll"). Ein leeres Team ist grün, nicht rot.
 */
function ableitenStatus(v: Verteilung): StatusLevel {
  if (v.total === 0) return "gruen";
  if (v.critical >= ROT_AB_KRITISCH) return "rot";
  if (v.warning / v.total > ROT_AB_GELB_ANTEIL) return "rot";
  if (v.warning > 0) return "gelb";
  return "gruen";
}

/** Einzeiler, der den abgeleiteten Zustand erklärt — nie nur die Farbe zeigen. */
function begruendung(v: Verteilung, status: StatusLevel): string {
  if (v.total === 0) return "Keine Mitarbeiter in deiner Berichtslinie";
  if (status === "rot" && v.critical > 0) {
    const rest = v.warning > 0 ? `, ${v.warning} auffällig` : "";
    return `${v.critical} von ${v.total} Mitarbeitern kritisch${rest}`;
  }
  if (status === "rot") {
    const anteil = Math.round((v.warning / v.total) * 100);
    return `${v.warning} von ${v.total} Mitarbeitern auffällig (${anteil} % — über der 20-%-Schwelle)`;
  }
  if (status === "gelb") {
    return `${v.warning} von ${v.total} Mitarbeitern auffällig, niemand kritisch`;
  }
  return v.total === 1
    ? "Der einzige Mitarbeiter ist im grünen Bereich"
    : `Alle ${v.total} Mitarbeiter im grünen Bereich`;
}

/** Icon je Zustand — zweites Signal neben Wort und Punkt. */
function StatusIcon(props: { status: StatusLevel; size: number }) {
  const color = STATUS_META[props.status].mark;
  if (props.status === "gruen")
    return <CircleCheck size={props.size} aria-hidden style={{ color }} />;
  if (props.status === "gelb") return <Eye size={props.size} aria-hidden style={{ color }} />;
  return <AlertTriangle size={props.size} aria-hidden style={{ color }} />;
}

/** Segmentierter Balken (2px Lücken) + Zahlen daneben. */
function VerteilungsLeiste(props: { v: Verteilung }) {
  const { v } = props;
  const segmente = AMPEL_META.filter((s) => v[s.key] > 0);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <div className="flex h-2 flex-1 min-w-[110px]" style={{ gap: 2 }} aria-hidden>
        {segmente.length === 0 ? (
          <span className="flex-1" style={{ background: "var(--bg-subtle)", borderRadius: 2 }} />
        ) : (
          segmente.map((s) => (
            <span key={s.key} style={{ flexGrow: v[s.key], background: s.mark, borderRadius: 2 }} />
          ))
        )}
      </div>
      <div className="flex items-center gap-2.5 flex-none">
        {AMPEL_META.map((s) => (
          <span
            key={s.key}
            className="flex items-center gap-1 text-[11px]"
            style={{ color: "var(--text-muted)" }}
          >
            <span
              aria-hidden
              className="w-2 h-2 rounded-full flex-none"
              style={{ background: s.mark, opacity: v[s.key] > 0 ? 1 : 0.35 }}
            />
            <b className="font-mono-data" style={{ color: "var(--text-primary)" }}>
              {v[s.key]}
            </b>
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Große Status-Karte: Zustand als Wort + Punkt + Icon, Begründung, Verteilung,
 * Absprung in die Tiefenseite. Echtes <a> (react-router `Link`), damit die
 * globale :focus-visible-Regel und Tastaturbedienung ohne Zutun greifen.
 */
function StatusKarte(props: {
  titel: string;
  icon: React.ReactNode;
  v: Verteilung;
  hinweis?: string;
  to: string;
  zielLabel: string;
}) {
  const status = ableitenStatus(props.v);
  const meta = STATUS_META[status];
  return (
    <Link
      to={props.to}
      className="card card-hover p-4 flex flex-col gap-3"
      style={{ textDecoration: "none", color: "inherit" }}
      title={`${props.titel}: ${meta.wort} — ${props.zielLabel}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className="text-[11px] font-bold tracking-widest uppercase flex items-center gap-1.5"
          style={{ color: "var(--text-muted)" }}
        >
          {props.icon}
          {props.titel}
        </span>
        <span
          className="text-[11px] font-medium flex items-center gap-1"
          style={{ color: "var(--accent)" }}
        >
          {props.zielLabel}
          <ArrowRight size={12} aria-hidden />
        </span>
      </div>

      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className="w-3.5 h-3.5 rounded-full flex-none"
          style={{ background: meta.mark }}
        />
        <StatusIcon status={status} size={20} />
        <span className="text-2xl font-bold tracking-tight" style={{ color: meta.text }}>
          {meta.wort}
        </span>
      </div>

      <p className="text-xs -mt-1.5" style={{ color: "var(--text-secondary)" }}>
        {begruendung(props.v, status)}
        {props.hinweis && <span style={{ color: "var(--text-muted)" }}> · {props.hinweis}</span>}
      </p>

      <VerteilungsLeiste v={props.v} />
    </Link>
  );
}

/** Vergleichs-Chip: aktueller Wert vs. Vorperiode / Zeitraum-Anfang. */
function DeltaChip(props: {
  value: number | null;
  unit?: string;
  invert?: boolean;
  title?: string;
}) {
  if (props.value === null || Number.isNaN(props.value)) return null;
  const v = props.value;
  const stable = Math.abs(v) < 0.05;
  const bad = props.invert ? v < 0 : v > 0;
  return (
    <span
      className="chip"
      title={props.title}
      style={{
        background: stable
          ? "var(--bg-subtle)"
          : bad
            ? "var(--risk-high-bg)"
            : "var(--risk-low-bg)",
        color: stable
          ? "var(--text-muted)"
          : bad
            ? "var(--risk-high-text)"
            : "var(--risk-low-text)",
      }}
    >
      {stable ? (
        "stabil"
      ) : (
        <>
          {v > 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
          {`${v > 0 ? "+" : ""}${v.toFixed(v % 1 === 0 ? 0 : 1)}${props.unit ?? ""}`}
        </>
      )}
    </span>
  );
}

/** KPI-Kachel mit optionaler Sparkline — der Trend wohnt IN der Kennzahl.
 *  `names`: Mouseover zeigt, wer hinter der Zahl steht (Marie, 27.07.).
 *  `accent` = Marker-Farbe (Linie oben), `accentText` = -text-Variante der Zahl. */
function KpiCard(props: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hero?: boolean;
  accent?: string;
  accentText?: string;
  chip?: React.ReactNode;
  sub?: React.ReactNode;
  spark?: number[];
  sparkColor?: string;
  names?: string[];
}) {
  const [hover, setHover] = useState(false);
  const names = props.names ?? [];
  const spark = props.spark?.filter((v) => !Number.isNaN(v)) ?? [];
  const showSpark = spark.length >= 2;
  const min = Math.min(...spark);
  const max = Math.max(...spark);
  const span = Math.max(max - min, 0.0001);
  const pts = spark
    .map((v, i) => `${(i / (spark.length - 1)) * 100},${28 - ((v - min) / span) * 24}`)
    .join(" ");
  return (
    <div
      className={`card flex-1 relative ${props.hero ? "min-w-[190px] p-4" : "min-w-[150px] p-3.5"}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {hover && names.length > 0 && (
        <div
          className="absolute left-2 top-full mt-1 z-20 p-2.5 min-w-[180px] max-w-[260px]"
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <div className="flex flex-col gap-1">
            {names.slice(0, 12).map((n) => (
              <span key={n} className="text-[11px]" style={{ color: "var(--text-primary)" }}>
                {n}
              </span>
            ))}
            {names.length > 12 && (
              <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                + {names.length - 12} weitere
              </span>
            )}
          </div>
        </div>
      )}
      {props.accent && (
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-0.5"
          style={{
            background: props.accent,
            opacity: 0.9,
            borderRadius: "var(--radius-lg) var(--radius-lg) 0 0",
          }}
        />
      )}
      <div className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--text-muted)" }}>
        {props.icon}
        {props.label}
      </div>
      <div className="flex items-baseline gap-2 mt-1.5">
        <span
          className={`${props.hero ? "text-3xl" : "text-2xl"} font-bold font-mono-data tracking-tight`}
          style={{ color: props.accentText ?? "var(--text-primary)" }}
        >
          {props.value}
        </span>
        {props.chip}
      </div>
      {/* Sparkline ist rein dekorativ: Wert und Delta stehen als Text darueber.
          Deshalb aria-hidden statt eines redundanten Titels. */}
      {showSpark && (
        <svg
          aria-hidden="true"
          viewBox="0 0 100 30"
          preserveAspectRatio="none"
          className="w-full h-6 mt-1.5"
        >
          <polyline
            points={pts}
            fill="none"
            stroke={props.sparkColor ?? "var(--accent)"}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
            opacity="0.9"
          />
        </svg>
      )}
      {props.sub && (
        <div className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>
          {props.sub}
        </div>
      )}
    </div>
  );
}

function Panel(props: {
  icon: React.ReactNode;
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <p
          className="text-[11px] font-bold tracking-widest uppercase flex items-center gap-1.5"
          style={{ color: "var(--text-muted)" }}
        >
          {props.icon}
          {props.title}
        </p>
        {props.right}
      </div>
      {props.children}
    </div>
  );
}

function Empty(props: { text: string }) {
  return (
    <p className="text-xs" style={{ color: "var(--text-muted)" }}>
      {props.text}
    </p>
  );
}

export function FkDashboard() {
  const today = iso(new Date());
  const [from, setFrom] = useState<string>(iso(new Date(Date.now() - 90 * DAY_MS)));
  const [to, setTo] = useState<string>(today);
  const [detailsOffen, setDetailsOffen] = useState(false);

  const activePreset = PRESETS.find(
    (p) => to === today && from === iso(new Date(Date.now() - p.days * DAY_MS)),
  );

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["fk-dashboard", from, to],
    queryFn: () => fkDashboardApi.get(from, to),
    staleTime: 5 * 60_000,
  });

  const { data: survey } = useQuery({
    queryKey: ["fk-survey-summary"],
    queryFn: () => surveyApi.getSummary(),
    staleTime: 10 * 60_000,
  });

  // Status-Kopf: gleicher queryKey wie in „Mein Team" — beide Seiten teilen
  // sich den Cache, der Absprung fühlt sich dadurch sofort an.
  const {
    data: team,
    isLoading: teamLoading,
    isError: teamError,
  } = useQuery({
    queryKey: ["mein-team"],
    queryFn: wirtschaftApi.getTeam,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const teamRows = team?.rows ?? [];
  const wirtschaftVerteilung = zaehleAmpeln(teamRows.map((r) => r.wirtschaft.level));
  // Bestätigte Kündigungen sind kein Bindungs-RISIKO mehr, sondern Fakt —
  // gleiche Konvention wie in „Mein Team", sonst färbt sich der Kopf rot für
  // etwas, das die Führungskraft längst weiß.
  const bindungRows = teamRows.filter((r) => !r.bindung.isConfirmedLeaving);
  const bindungVerteilung = zaehleAmpeln(bindungRows.map((r) => r.bindung.level));
  const bestaetigteAbgaenge = teamRows.length - bindungRows.length;

  const {
    data: patterns,
    isLoading: patternsLoading,
    isError: patternsError,
    refetch: refetchPatterns,
  } = useQuery({
    queryKey: ["fk-ai-patterns"],
    queryFn: () => aiApi.getPatterns(),
    staleTime: 30 * 60_000,
    retry: 0,
  });

  const series = data?.series ?? [];
  // Koernung passt sich dem Zeitraum an (Marie, 27.07.): kurze Zeitraeume in
  // Wochen-Staenden, lange in Monats-Staenden — sonst zeigen 30 Tage und
  // 12 Monate dasselbe Bild. Stand = letzter Schnappschuss des Buckets.
  const rangeDays = Math.round((new Date(to).getTime() - new Date(from).getTime()) / DAY_MS);
  const monthly = rangeDays > 92;
  const isoWeek = (isoDate: string): string => {
    const d = new Date(`${isoDate}T00:00:00Z`);
    const day = (d.getUTCDay() + 6) % 7; // Mo=0
    d.setUTCDate(d.getUTCDate() - day + 3); // Donnerstag der Woche
    const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
    const week = 1 + Math.round((d.getTime() - jan4.getTime()) / (7 * DAY_MS));
    return `${d.getUTCFullYear()}-KW${String(week).padStart(2, "0")}`;
  };
  const buckets = new Map<string, (typeof series)[number]>();
  for (const pt of series) {
    buckets.set(monthly ? pt.date.slice(0, 7) : isoWeek(pt.date), pt);
  }
  const chartData = [...buckets.entries()]
    .sort(([, a], [, b]) => a.date.localeCompare(b.date))
    .map(([key, pt]) => ({
      ...pt,
      label: monthly
        ? new Date(`${key}-01`).toLocaleDateString("de-DE", { month: "short", year: "2-digit" })
        : `KW ${key.slice(-2)}`,
    }));
  const firstS = series[0];
  const lastS = series[series.length - 1];
  const scoreDelta =
    firstS?.avgScore != null && lastS?.avgScore != null && series.length > 1
      ? (lastS.avgScore - firstS.avgScore) * 100
      : null;

  const maxCount = data?.signalRanking[0]?.count ?? 1;
  // Ehrlichkeits-Hinweis: die taeglichen Schnappschuesse existieren erst seit
  // Anfang Juni 2026 — reicht der gewaehlte Zeitraum weiter zurueck, decken
  // Trend/Sparklines nur den verfuegbaren Teil ab.
  const seriesFrom = series[0]?.date ?? null;
  const coverageGap = seriesFrom != null && seriesFrom > from;
  const fmtDe = (isoDate: string) =>
    new Date(isoDate).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  const participation =
    survey?.available && survey.participationRate != null ? survey.participationRate : null;

  return (
    <AppLayout>
      <Header
        title="FK-Dashboard"
        subtitle="Ist dein Team im grünen Bereich? — Bindung und Wirtschaftlichkeit auf einen Blick"
      />

      {/* F21 (26.08.2026): `flex-1 overflow-y-auto` ist Pflicht. AppLayout setzt
          zweimal `overflow-hidden` (Rahmen und <main>), jede Seite muss ihren
          eigenen Scroll-Bereich mitbringen. Fehlte hier — dadurch war alles
          unterhalb der Fensterhöhe unerreichbar, bei über 500 Zeilen Inhalt. */}
      <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5 stagger">
        {/* ── Zone 0: Status-Kopf — die Antwort vor den Daten ── */}
        {teamLoading ? (
          <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }} aria-hidden>
            <div className="skeleton h-[150px]" />
            <div className="skeleton h-[150px]" />
          </div>
        ) : teamError ? (
          // Bewusst ruhig: eine fehlende Datenlieferung ist kein Team-Risiko.
          <div className="card p-4">
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              Statusübersicht gerade nicht verfügbar — die Details unten funktionieren weiterhin.
            </p>
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <StatusKarte
              titel="Bindung"
              icon={<Radar size={13} />}
              v={bindungVerteilung}
              hinweis={
                bestaetigteAbgaenge > 0
                  ? `${bestaetigteAbgaenge} bestätigte ${
                      bestaetigteAbgaenge === 1 ? "Kündigung" : "Kündigungen"
                    } (nicht gewertet)`
                  : ""
              }
              to="/retention"
              zielLabel="Retention Radar"
            />
            <StatusKarte
              titel="Wirtschaftlichkeit"
              icon={<Calculator size={13} />}
              v={wirtschaftVerteilung}
              to="/wirtschaftlichkeit"
              zielLabel="Wirtschaftlichkeit"
            />
          </div>
        )}

        {/* ── Absprung in die Tiefe: alles Bisherige liegt hier drunter ── */}
        <button
          type="button"
          onClick={() => setDetailsOffen((offen) => !offen)}
          aria-expanded={detailsOffen}
          className="card card-hover w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left"
        >
          <span
            className="text-[11px] font-bold tracking-widest uppercase"
            style={{ color: "var(--text-muted)" }}
          >
            {detailsOffen ? "Details ausblenden" : "Details anzeigen"}
          </span>
          <span
            className="text-[11px] flex items-center gap-1.5"
            style={{ color: "var(--text-muted)" }}
          >
            Kennzahlen, Risiko-Verlauf, Signale und KI-Muster
            <ChevronDown
              size={14}
              aria-hidden
              style={{
                transform: detailsOffen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform var(--dur) var(--ease)",
              }}
            />
          </span>
        </button>

        {detailsOffen && (
          <>
            {/* ── Zeitraum: Presets + frei wählbar ── */}
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-[11px] font-bold uppercase tracking-widest"
                style={{ color: "var(--text-muted)" }}
              >
                Zeitraum
              </span>
              <div className="segmented">
                {PRESETS.map((p) => (
                  <button
                    key={p.days}
                    type="button"
                    data-active={activePreset?.days === p.days}
                    onClick={() => {
                      setFrom(iso(new Date(Date.now() - p.days * DAY_MS)));
                      setTo(today);
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <input
                type="date"
                value={from}
                max={to}
                onChange={(e) => e.target.value && setFrom(e.target.value)}
                className="px-2 py-1 text-xs"
                style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  color: "var(--text-primary)",
                }}
              />
              <span style={{ color: "var(--text-muted)" }}>–</span>
              <input
                type="date"
                value={to}
                min={from}
                max={today}
                onChange={(e) => e.target.value && setTo(e.target.value)}
                className="px-2 py-1 text-xs"
                style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  color: "var(--text-primary)",
                }}
              />
              <button
                type="button"
                onClick={() => void refetch()}
                className="ml-auto flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium row-hover"
                style={{
                  background: "transparent",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  color: "var(--text-muted)",
                }}
                title="Aktualisieren"
              >
                <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} />
                {isFetching ? "Lädt…" : "Aktualisieren"}
              </button>
            </div>

            {isLoading && (
              <div className="flex flex-col gap-5" aria-hidden>
                <div className="flex flex-wrap gap-4">
                  {["k1", "k2", "k3", "k4", "k5", "k6"].map((k) => (
                    <div key={k} className="skeleton flex-1 min-w-[150px] h-[110px]" />
                  ))}
                </div>
                <div className="skeleton h-[240px]" />
              </div>
            )}
            {isError && (
              <p className="text-xs" style={{ color: "var(--risk-high-text)" }}>
                Daten konnten nicht geladen werden.
              </p>
            )}

            {data && (
              <>
                {/* ── Zone 1: KPIs mit eingebautem Trend + Hover-Namen ── */}
                <div className="flex flex-wrap gap-4">
                  <KpiCard
                    icon={<Users size={12} />}
                    label="Mitarbeiter aktiv"
                    value={String(data.teamSize)}
                    chip={
                      <DeltaChip
                        value={data.teamSizeVorjahr ? data.teamSize - data.teamSizeVorjahr : null}
                        invert
                        title={`vs. Bestand am ${fmtDe(data.vergleichBis)} (${data.teamSizeVorjahr})`}
                      />
                    }
                    sub="Wachstum vs. Vorjahr"
                  />
                  <KpiCard
                    icon={<UserPlus size={12} />}
                    label="Onboarding"
                    value={String(data.zugaengeImZeitraum)}
                    accent="var(--risk-low)"
                    accentText="var(--risk-low-text)"
                    chip={
                      <DeltaChip
                        value={
                          data.zugaengeVorperiode || data.zugaengeImZeitraum
                            ? data.zugaengeImZeitraum - data.zugaengeVorperiode
                            : null
                        }
                        invert
                        title={`vs. Vorjahreszeitraum ${fmtDe(data.vergleichVon)} – ${fmtDe(data.vergleichBis)} (${data.zugaengeVorperiode} Zugänge)`}
                      />
                    }
                    names={data.namen.zugaenge}
                    sub={
                      <span className="flex items-center gap-1.5 flex-wrap">
                        Frühfluktuation 12 M:{" "}
                        <b className="font-mono-data" style={{ color: "var(--text-primary)" }}>
                          {data.einstellungen12m > 0
                            ? `${Math.round((data.fruehAustritte12m / data.einstellungen12m) * 100)} %`
                            : "—"}
                        </b>
                        <DeltaChip
                          value={
                            data.einstellungen12m > 0 && data.einstellungenVorjahr > 0
                              ? (data.fruehAustritte12m / data.einstellungen12m -
                                  data.fruehAustritteVorjahr / data.einstellungenVorjahr) *
                                100
                              : null
                          }
                          unit=" pp"
                          title={`vs. Vorjahres-Kohorte (${data.fruehAustritteVorjahr}/${data.einstellungenVorjahr} früh gegangen)`}
                        />
                      </span>
                    }
                  />
                  <KpiCard
                    icon={<UserMinus size={12} />}
                    label="Abgänge im Zeitraum"
                    value={String(data.abgaengeImZeitraum)}
                    accent={data.abgaengeImZeitraum > 0 ? "var(--risk-high)" : undefined}
                    accentText={data.abgaengeImZeitraum > 0 ? "var(--risk-high-text)" : undefined}
                    chip={
                      <DeltaChip
                        value={
                          data.abgaengeVorperiode || data.abgaengeImZeitraum
                            ? data.abgaengeImZeitraum - data.abgaengeVorperiode
                            : null
                        }
                        title={`vs. Vorjahreszeitraum ${fmtDe(data.vergleichVon)} – ${fmtDe(data.vergleichBis)} (${data.abgaengeVorperiode} Abgänge)`}
                      />
                    }
                    names={[...data.namen.abgaenge, ...data.namen.bestaetigteAbgaenge]}
                    sub={
                      data.confirmedLeaving > 0
                        ? `+ ${data.confirmedLeaving} bestätigte kommende`
                        : "vs. Vorjahreszeitraum"
                    }
                  />
                  <KpiCard
                    icon={<TrendingUp size={12} />}
                    label="Fluktuation (12 M)"
                    value={`${(data.fluktuation12m * 100).toFixed(1)}%`}
                    accent={data.fluktuation12m >= 0.15 ? "var(--risk-medium)" : undefined}
                    accentText={data.fluktuation12m >= 0.15 ? "var(--risk-medium-text)" : undefined}
                    names={data.namen.abgaenge12m}
                    sub={`${data.abgaenge12m} Abgänge · ${data.bedauerlich12m} bedauerlich`}
                  />
                  <KpiCard
                    hero
                    icon={<AlertTriangle size={12} />}
                    label="Risikolage"
                    value={data.avgScore != null ? `${Math.round(data.avgScore * 100)} %` : "—"}
                    accent={
                      data.avgScore == null
                        ? undefined
                        : data.avgScore >= 0.7
                          ? "var(--risk-high)"
                          : data.avgScore >= 0.45
                            ? "var(--risk-medium)"
                            : "var(--risk-low)"
                    }
                    accentText={
                      data.avgScore == null
                        ? undefined
                        : data.avgScore >= 0.7
                          ? "var(--risk-high-text)"
                          : data.avgScore >= 0.45
                            ? "var(--risk-medium-text)"
                            : "var(--risk-low-text)"
                    }
                    chip={
                      <DeltaChip
                        value={scoreDelta}
                        unit=" pp"
                        title="Ø Risiko-Score vs. Anfang des gewählten Zeitraums"
                      />
                    }
                    names={data.namen.hochrisiko}
                    sub={
                      data.highRisk > 0
                        ? `${data.highRisk} Mitarbeiter mit hohem Risiko`
                        : "kein Mitarbeiter mit hohem Risiko"
                    }
                  />
                  <KpiCard
                    icon={<ClipboardList size={12} />}
                    label="Puls-Teilnahme"
                    value={participation != null ? `${Math.round(participation * 100)}%` : "—"}
                    accent={
                      participation != null && participation < 0.7
                        ? "var(--risk-medium)"
                        : undefined
                    }
                    accentText={
                      participation != null && participation < 0.7
                        ? "var(--risk-medium-text)"
                        : undefined
                    }
                    sub={survey?.round ? `Runde ${survey.round}` : "keine Runde verfügbar"}
                  />
                </div>

                {/* ── Zone 2: Risiko-Verteilungs-Fluss ── */}
                <Panel
                  icon={<TrendingUp size={13} />}
                  title={monthly ? "Risiko-Verteilung pro Monat" : "Risiko-Verteilung pro Woche"}
                  right={
                    <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                      {coverageGap && seriesFrom
                        ? `Verlaufsdaten erst seit ${fmtDe(seriesFrom)} — bauen sich täglich auf`
                        : "Wer bewegt sich zwischen grün, gelb und rot?"}
                    </span>
                  }
                >
                  {chartData.length >= 1 ? (
                    <div style={{ width: "100%", height: 240 }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={chartData}
                          margin={{ top: 6, right: 8, left: -18, bottom: 0 }}
                          barCategoryGap="28%"
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="var(--chart-grid)"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 10, fill: "var(--text-muted)" }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <Tooltip
                            cursor={{ fill: "var(--bg-hover)", opacity: 0.4 }}
                            contentStyle={{
                              background: "var(--bg-card)",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius)",
                              boxShadow: "var(--shadow-md)",
                              fontSize: 12,
                            }}
                            formatter={(value: number, name: string) => [
                              `${value} Mitarbeiter`,
                              RISK_LABELS[name] ?? name,
                            ]}
                            labelFormatter={(l) =>
                              monthly ? `Monatsstand ${l}` : `Wochenstand ${l}`
                            }
                          />
                          <Legend
                            iconType="circle"
                            iconSize={8}
                            wrapperStyle={{ fontSize: 11, color: "var(--text-secondary)" }}
                            formatter={(value) => RISK_LABELS[String(value)] ?? String(value)}
                          />
                          <Bar
                            dataKey="low"
                            stackId="risk"
                            fill={RISK_COLORS.low}
                            maxBarSize={28}
                            radius={[0, 0, 4, 4]}
                          />
                          <Bar
                            dataKey="medium"
                            stackId="risk"
                            fill={RISK_COLORS.medium}
                            maxBarSize={28}
                          />
                          <Bar
                            dataKey="high"
                            stackId="risk"
                            fill={RISK_COLORS.high}
                            maxBarSize={28}
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Empty text="Noch keine Schnappschuss-Monate im gewählten Zeitraum — der Verlauf baut sich täglich auf." />
                  )}
                </Panel>

                {/* ── Zone 3: Muster ── */}
                <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <Panel
                    icon={<MessageSquareWarning size={13} />}
                    title="Häufigste Signale in deiner Berichtslinie"
                  >
                    {data.signalRanking.length === 0 ? (
                      <Empty text="Aktuell ist kein Risiko-Signal ausgelöst — gutes Zeichen." />
                    ) : (
                      <div className="flex flex-col gap-2.5">
                        {data.signalRanking.map((s, idx) => (
                          <div key={s.key} className="flex items-center gap-2">
                            <span
                              className="text-[10px] font-bold font-mono-data w-4 text-right"
                              style={{ color: idx < 3 ? "var(--accent)" : "var(--text-muted)" }}
                            >
                              {idx + 1}
                            </span>
                            <span
                              className="text-xs flex-1 truncate"
                              style={{ color: "var(--text-secondary)" }}
                              title={s.label}
                            >
                              {s.label}
                            </span>
                            <div
                              className="h-2.5 rounded-full"
                              style={{
                                width: `${Math.max(10, (s.count / maxCount) * 130)}px`,
                                background:
                                  "linear-gradient(90deg, var(--accent), color-mix(in srgb, var(--accent) 55%, transparent))",
                              }}
                            />
                            <span
                              className="text-xs font-bold font-mono-data w-6 text-right"
                              style={{ color: "var(--text-primary)" }}
                            >
                              {s.count}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Panel>

                  <Panel
                    icon={<Sparkles size={13} />}
                    title="KI-Musteranalyse"
                    right={
                      <button
                        type="button"
                        onClick={() => void refetchPatterns()}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] row-hover"
                        style={{
                          background: "transparent",
                          border: "1px solid var(--border)",
                          borderRadius: "var(--radius)",
                          color: "var(--text-muted)",
                        }}
                      >
                        <RefreshCw size={10} className={patternsLoading ? "animate-spin" : ""} />
                        Neu analysieren
                      </button>
                    }
                  >
                    {patternsLoading && (
                      <div className="flex flex-col gap-2" aria-hidden>
                        <div className="skeleton h-3 w-4/5" />
                        <div className="skeleton h-3 w-3/5" />
                        <div className="skeleton h-3 w-2/3" />
                      </div>
                    )}
                    {patternsError && (
                      <Empty text="KI-Analyse nicht verfügbar — später erneut versuchen." />
                    )}
                    {patterns && (
                      <div className="flex flex-col gap-2">
                        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                          {patterns.summary}
                        </p>
                        {patterns.insights.slice(0, 3).map((i) => (
                          <div key={i.id} className="flex items-start gap-2">
                            <span
                              className="mt-1 w-2 h-2 rounded-full flex-none"
                              style={{ background: SEVERITY_COLOR[i.severity] ?? "var(--accent)" }}
                            />
                            <div>
                              <p
                                className="text-xs font-medium"
                                style={{ color: "var(--text-primary)" }}
                              >
                                {i.title}
                              </p>
                              <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                                {i.recommendation}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Panel>
                </div>

                <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                  Zahlen deiner Berichtslinie · Details: Retention Radar, Onboarding, KPI Reporting,
                  Pulsumfrage
                </p>
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
